import type { Escopo } from './escopo';
import { ehDoEscopo, TAG_EMPRESA, temTagEmpresa } from './escopo';

/**
 * Uma linha do mês, como a query entrega.
 * Valores em CENTAVOS: positivo = entrou, negativo = saiu.
 */
export type LinhaDoMes = {
  id: string;
  date: string;
  amount: number;
  notes: string | null;
  /**
   * ⚠️⚠️ ACHADO DA TASK 7 (confirmado ao vivo contra o app real e contra o
   * código de produção `packages/loot-core/src/server/transactions/
   * transfer.ts`): no dado CRU do Actual, `transfer_id` é um PONTEIRO PRA
   * IRMÃ — cada perna guarda o `id` da OUTRA (`A.transfer_id === B.id` e
   * `B.transfer_id === A.id`). NUNCA é um id de evento compartilhado pelas
   * duas pernas, apesar de ser tentador ler assim.
   *
   * `resumirMes` correlaciona as duas pernas internamente (função
   * `canonizarTransferId`, chamada logo no início da função) — quem monta
   * `LinhaDoMes[]` a partir de uma query só precisa passar o campo cru como
   * veio do banco; não precisa (e não deve) pré-processá-lo.
   *
   * Quando a irmã não vem no MESMO lote de `linhas` (ex.: caiu em outro mês
   * porque alguém editou só a data de uma das pernas — `updateTransfer`
   * nunca sincroniza `date` entre as duas), `resumirMes` sozinho não tem
   * como saber se aquilo é aporte ou remanejo. Ver `idsDeIrmasOrfas` e
   * `Extras.contaDaIrmaOrfa`.
   */
  transfer_id: string | null;
  /** Preenchido quando a transação veio de um agendado (= conta fixa). */
  schedule: string | null;
  category: string | null;
  /** A conta desta linha está fora do orçamento (offbudget). */
  contaForaDoOrcamento: boolean;
  /** Transação de saldo inicial criada ao abrir a conta. */
  ehSaldoInicial?: boolean;
  /** Linha-pai de um split: o valor real está nos filhos. */
  ehPai?: boolean;
  ehFilho?: boolean;
};

export type ResumoDoMes = {
  entrou: number;
  gastosVariaveis: number;
  fixasRealizadas: number;
  fixasPrevistas: number;
  aportes: number;
  /** entrou − fixas (realizadas + previstas) − aportes − gastos variáveis */
  livre: number;
  naoEhGasto: {
    transferencias: number;
    empresa: number;
    /**
     * Perna de transferência ÓRFÃ (a irmã não veio no lote) cujo destino —
     * aporte, remanejo puro, ou nenhum dos dois — não pôde ser determinado
     * por falta da informação em `Extras.contaDaIrmaOrfa`.
     *
     * ⚠️⚠️ NUNCA vira "transferência" por padrão: assumir remanejo sem saber
     * escondia um aporte real (dinheiro saindo de vez do orçamento) e fazia
     * o "livre" ficar sistematicamente ALTO DEMAIS — a direção perigosa
     * (nunca subestima, sempre superestima). Fica visível aqui em vez de
     * ser adivinhado, e NÃO entra na conta de `livre`.
     *
     * ⚠️⚠️⚠️ OBRIGATÓRIO NA TELA (Fix round 2 da Task 7, Finding 5):
     * `naoClassificado` TEM que aparecer renderizado onde quer que
     * `ResumoDoMes` seja mostrado (ex.: `/inicio`, Task 9 em diante). Um
     * balde que só existe no objeto e não é exibido é dado ESCONDIDO — e aí
     * o "livre" volta a superestimar em silêncio, exatamente o que este
     * campo existe pra evitar. Não é opcional pra quem consumir este tipo.
     */
    naoClassificado: number;
  };
};

export type Extras = {
  /** Soma das contas fixas do mês que ainda NÃO foram pagas (vem dos agendados). */
  fixasPrevistas?: number;
  /**
   * Off-budget da conta da IRMÃ de uma perna de transferência cuja irmã NÃO
   * veio no lote de `linhas` (ex.: a irmã caiu em outro mês porque alguém
   * editou só a `date` de uma das pernas — `updateTransfer`, em
   * `transfer.ts`, nunca sincroniza a data entre as duas, só `account`,
   * `payee`, `notes`, `amount` e `schedule`; editar a data é uma ação de UI
   * banal, então esse cenário não é raro).
   *
   * Chave = o `transfer_id` da perna PRESENTE no lote (que, pela semântica
   * real do campo, é o `id` da irmã ausente). Valor = true se a CONTA da
   * irmã é fora do orçamento.
   *
   * Quem popula isto é o chamador (o hook), com uma consulta separada e
   * ENXUTA — só `id` + `account.offbudget` da(s) irmã(s) ausente(s), nunca a
   * transação inteira: trazer a transação junto inflaria `entrou`/
   * `gastosVariaveis` deste mês com dado de OUTRO mês. Ver `idsDeIrmasOrfas`,
   * que devolve exatamente os ids que precisam dessa consulta.
   *
   * Sem esta informação (chave ausente do mapa, ou o mapa inteiro ausente —
   * o valor default de `resumirMes`), a perna órfã cai em
   * `naoEhGasto.naoClassificado`, nunca é adivinhada.
   */
  contaDaIrmaOrfa?: Record<string, boolean>;
};

/**
 * ⚠️⚠️ Fix Round 1 da Task 11 (Finding 4) — as 3 guardas abaixo eram
 * DUPLICADAS entre `resumirMes` (como acesso direto ao campo, `l.ehPai`
 * etc.) e `agruparPorCategoria` (`porCategoria.ts`), que simplesmente não
 * as tinha. A régua JÁ divergiu uma vez — é o que produziu os Findings 1-3
 * daquele round (split dobrando o total, saldo inicial virando "gasto de
 * categoria", conta fora do orçamento entrando na conta). Ficam privadas
 * (não exportadas) de propósito: `resumirMes` continua chamando cada uma
 * delas EXATAMENTE nos 3 pontos onde antes lia o campo cru — é uma troca
 * de `l.ehPai` por `ehLinhaPai(l)`, nada de reordenação — para não repetir
 * os dois fix rounds de dupla contagem de transferência que este arquivo
 * já levou. Quem precisa de UMA função só que já responde "esta linha
 * conta como gasto?" é `ehGastoDoOrcamento`, logo abaixo — construída em
 * cima destas três + a checagem de transferência/sinal/escopo que É
 * idêntica à de `agruparPorCategoria` mas que `resumirMes` NÃO pode
 * delegar a uma função só (ele precisa saber o MOTIVO da exclusão: o
 * balde "empresa" só soma quando a exclusão é por ESCOPO, e transferência
 * vira um dos 4 baldes possíveis, nunca um "não conta" mudo).
 */
function ehLinhaPai(l: LinhaDoMes): boolean {
  return Boolean(l.ehPai);
}

function ehTransacaoDeSaldoInicial(l: LinhaDoMes): boolean {
  return Boolean(l.ehSaldoInicial);
}

function contaEhForaDoOrcamento(l: LinhaDoMes): boolean {
  return Boolean(l.contaForaDoOrcamento);
}

/**
 * ⭐⭐ Fonte única de "esta linha conta como gasto do orçamento, neste
 * escopo?" — pensada para quem só precisa da resposta em bloco, sem
 * distinguir fixa×variável nem classificar transferência em
 * aporte/remanejo/não-classificado (o caso de `agruparPorCategoria`, em
 * `porCategoria.ts`).
 *
 * `resumirMes` NÃO usa esta função internamente — ela junta, numa única
 * pergunta, cinco condições que `resumirMes` precisa examinar em MOMENTOS
 * diferentes do próprio loop (`contaForaDoOrcamento` só é checado depois do
 * ramo de transferência, porque uma transferência PODE ter a conta
 * receptora fora do orçamento — é assim que um aporte é detectado; se
 * `contaForaDoOrcamento` fosse checado ANTES do ramo de transferência,
 * TODO aporte pararia de ser reconhecido). Achatar as duas funções numa só
 * exigiria reescrever essa ordem — o tipo de mudança estrutural que este
 * arquivo já pagou caro duas vezes (os dois fix rounds de dupla contagem
 * de transferência da Task 7). Por isso só as 3 guardas que SÃO idênticas
 * nas duas funções (linha-pai, saldo inicial, conta fora do orçamento —
 * acima) foram extraídas; o resto (transferência, sinal do valor, escopo)
 * cada função decide à sua maneira, ainda que cheguem à mesma resposta
 * para o caso comum.
 */
export function ehGastoDoOrcamento(l: LinhaDoMes, escopo: Escopo): boolean {
  if (ehTransacaoDeSaldoInicial(l)) return false;
  if (ehLinhaPai(l)) return false;
  if (l.transfer_id) return false;
  if (contaEhForaDoOrcamento(l)) return false;
  if (l.amount >= 0) return false;
  return ehDoEscopo(l.notes, escopo);
}

/**
 * Canoniza o `transfer_id` das DUAS pernas de uma transferência, quando as
 * duas estão no MESMO lote de `linhas`.
 *
 * Pra um par (A, B) no dado real do Actual: `A.transfer_id === B.id` e
 * `B.transfer_id === A.id` — cada um aponta pro OUTRO, nunca um valor igual
 * nas duas (ver o JSDoc de `LinhaDoMes.transfer_id`). Sem canonizar, os Sets
 * internos de `resumirMes` (que agrupam por `transfer_id` como se fosse uma
 * chave compartilhada) comparam cada perna contra a chave ERRADA — a da
 * OUTRA perna, que nunca bate com a própria — e o aporte nunca é reconhecido.
 *
 * A chave canônica é o MENOR dos dois ids (só precisa ser determinística e
 * igual nas duas pernas — cada uma enxerga a MESMA dupla, só trocada de
 * lugar, então as duas calculam a mesma chave).
 *
 * ⚠️ Se a irmã não estiver no lote (fora da janela do mês, ou já apagada), a
 * linha é devolvida como veio — a canonização não tem com quem parear. Isso
 * é tratado à parte por `idsDeIrmasOrfas` + `Extras.contaDaIrmaOrfa`.
 *
 * ⚠️ É TRANSPARENTE pro estilo de fixture com `transfer_id` compartilhado
 * (usado nos testes deste arquivo, ex. `transfer_id: 'tx-1'` nas duas
 * pernas): como nenhuma linha tem `id === 'tx-1'`, a busca por id falha e as
 * linhas voltam inalteradas — mas elas JÁ compartilham a chave, então o
 * agrupamento por `transfer_id` funciona sem precisar de canonização nesse
 * caso. Isso não é coincidência: só canoniza quando encontra de verdade o
 * `id` da irmã no lote.
 */
export function canonizarTransferId(linhas: LinhaDoMes[]): LinhaDoMes[] {
  const porId = new Map(linhas.map(l => [l.id, l]));

  return linhas.map(l => {
    if (!l.transfer_id) return l;

    const irma = porId.get(l.transfer_id);
    if (!irma) return l;

    const chave = l.id < irma.id ? l.id : irma.id;
    return { ...l, transfer_id: chave };
  });
}

/**
 * Ids das irmãs que faltam no lote — para o CHAMADOR (o hook) saber quais
 * buscar numa consulta de apoio (ver `Extras.contaDaIrmaOrfa`).
 *
 * Canoniza internamente e considera uma perna ÓRFÃ quando, mesmo depois de
 * canonizada, NENHUMA outra linha do lote compartilha o mesmo `transfer_id`
 * — ou seja, nem achou a irmã pelo id (semântica real de ponteiro), nem o
 * lote já trazia um valor igual em duas linhas (estilo sintético dos
 * testes deste arquivo). Essa é a MESMA régua que `resumirMes` usa
 * internamente pra decidir "pareada vs. órfã" — as duas regras têm que
 * concordar, senão o hook buscaria (ou deixaria de buscar) a irmã errada.
 */
export function idsDeIrmasOrfas(linhas: LinhaDoMes[]): string[] {
  const canonizadas = canonizarTransferId(linhas);

  const contagem = new Map<string, number>();
  for (const l of canonizadas) {
    if (l.transfer_id) {
      contagem.set(l.transfer_id, (contagem.get(l.transfer_id) ?? 0) + 1);
    }
  }

  const orfaos = new Set<string>();
  for (const l of canonizadas) {
    if (l.transfer_id && (contagem.get(l.transfer_id) ?? 0) < 2) {
      orfaos.add(l.transfer_id);
    }
  }
  return [...orfaos];
}

/**
 * Classifica uma perna ÓRFÃ, dado (quando se sabe) se a conta da irmã é
 * fora do orçamento.
 *
 * ⚠️⚠️ Fix round 2 da Task 7 (Finding 5, Critical) — achado pelo re-revisor:
 * só a direção "dentro do orçamento SAINDO" pode virar aporte. A direção
 * espelhada ("fora do orçamento ENTRANDO") NUNCA gera aporte por si só,
 * com ou sem saber da irmã — mesmo quando a irmã É confirmada dentro do
 * orçamento.
 *
 * O princípio, endossado pelo re-revisor: **aporte é a SAÍDA do orçamento,
 * não a entrada no patrimônio.** Numa transferência cross-mês (a irmã caiu
 * em outro mês porque `updateTransfer` nunca sincroniza a `date` entre as
 * duas pernas — ver `LinhaDoMes.transfer_id`), o dinheiro sai do orçamento
 * no mês M1 (a perna que sai) e chega na conta fora do orçamento no mês M2
 * (a perna que entra) — meses DIFERENTES, cada um resolvido por uma chamada
 * INDEPENDENTE de `resumirMes`. Antes deste fix, a perna que ENTRA também
 * gerava aporte quando a irmã era confirmada dentro do orçamento — dobrando
 * o mesmo real no agregado dos dois meses (rM1.aportes + rM2.aportes = o
 * DOBRO do valor real), o oposto do que o fix round 1 prometia (nunca
 * subestima, sempre superestima — agora subestimava em dobro, na direção
 * contrária). A perna que sai já é sinal suficiente, sozinha, no mês dela,
 * de que o dinheiro deixou o orçamento; contar de novo do lado de quem
 * recebe é a MESMA informação, só que no mês errado.
 *
 * Por isso "fora do orçamento, entrando" é SEMPRE `nenhum` quando órfã,
 * independente da conta da irmã: se a irmã é dentro do orçamento, o aporte
 * já foi (ou será) contado no mês DELA; se a irmã também é fora do
 * orçamento, nunca houve saída do orçamento (é remanejo entre contas de
 * patrimônio) — os dois casos dão na mesma aqui, e por isso nem precisa
 * olhar `irmaForaDoOrcamento` pra essa direção.
 *
 * Só "dentro do orçamento, saindo" continua ambíguo e precisa da irmã:
 * remanejo comum se ela é dentro do orçamento, aporte se ela é fora.
 */
function classificarPernaOrfa(
  l: LinhaDoMes,
  irmaForaDoOrcamento: boolean | undefined,
): {
  tipo: 'aporte' | 'transferencia' | 'nenhum' | 'naoClassificado';
  valor: number;
} {
  // "Fora do orçamento, entrando" nunca soma aporte por si só — ver o JSDoc
  // acima (Fix round 2, Finding 5). Nem olha `irmaForaDoOrcamento`: a
  // resposta é a mesma nos dois casos (irmã dentro ou fora do orçamento).
  if (l.contaForaDoOrcamento && l.amount > 0) {
    return { tipo: 'nenhum', valor: 0 };
  }

  // A ÚNICA direção que pode virar aporte é "dentro do orçamento, saindo".
  const ambiguo = !l.contaForaDoOrcamento && l.amount < 0;

  if (!ambiguo) return { tipo: 'nenhum', valor: 0 };

  if (irmaForaDoOrcamento === undefined) {
    return { tipo: 'naoClassificado', valor: Math.abs(l.amount) };
  }

  return irmaForaDoOrcamento
    ? { tipo: 'aporte', valor: -l.amount }
    : { tipo: 'transferencia', valor: -l.amount };
}

/**
 * O número-herói do mês.
 *
 * Fórmula travada em 25/08/2026 e reafirmada no design de 05/09:
 *   livre = entrou − fixas do mês − aportes − gastos variáveis
 *
 * ⛔ Investimento, transferência, fatura de cartão e operação da empresa FICAM FORA.
 * No Actual os três primeiros já saem sozinhos (transferência tem transfer_id;
 * cartão é conta, então o gasto conta na compra). Sobra a tag #empresa.
 *
 * REGRA 2 do motor: fixa não paga entra pelo PLANEJADO (`extras.fixasPrevistas`),
 * fixa paga entra pelo REALIZADO. Sem isso o "livre" incha no começo do mês
 * e despenca no dia 15.
 *
 * ⚠️ Fix round 1 (revisão 05/09/2026): a tag #empresa decide de quem é o dinheiro,
 * e isso vale para QUALQUER linha — transferência e aporte inclusive. Uma
 * transferência é UM evento partido em duas linhas (uma por conta); as duas podem
 * chegar com `notes` diferentes (o Actual não garante notes sincronizadas entre as
 * duas pontas), e quem decide se é aporte é a perna RECEPTORA (fora do orçamento)
 * — que raramente é onde o usuário edita a nota do dia a dia. Por isso o escopo de
 * uma transferência é do EVENTO inteiro (se qualquer perna do transfer_id tem a
 * tag, o transfer_id inteiro é #empresa) — checar só a linha isolada faria um
 * aporte marcado numa perna só sumir dos dois escopos ao mesmo tempo (nem pessoal,
 * nem empresa contariam o real que existe uma vez).
 *
 * ⚠️⚠️ Fix round 1 da Task 7 (05/09/2026, achado ao vivo contra o app real): antes
 * de decidir aporte × transferência × nenhum, as linhas passam por
 * `canonizarTransferId` — sem isso, o `transfer_id` cru (que é um PONTEIRO PRA
 * IRMÃ, não um id compartilhado) faz os Sets abaixo comparar cada perna contra a
 * chave errada, e o aporte nunca é reconhecido. E quando a irmã não está no MESMO
 * lote, a perna cai no tratamento de "órfã" (`classificarPernaOrfa`), que só
 * classifica com certeza se `extras.contaDaIrmaOrfa` trouxer a informação — sem
 * ela, vai pro balde `naoClassificado`, nunca é adivinhada como transferência.
 */
export function resumirMes(
  linhas: LinhaDoMes[],
  escopo: Escopo,
  extras: Extras = {},
): ResumoDoMes {
  const canonizadas = canonizarTransferId(linhas);

  // Frequência de cada transfer_id (pós-canonização) — decide se uma perna
  // está de fato PAREADA dentro do lote (as duas presentes, ou o estilo
  // sintético de valor compartilhado) ou ÓRFÃ (precisa de `extras`).
  const contagemTransferId = new Map<string, number>();
  for (const l of canonizadas) {
    if (l.transfer_id) {
      contagemTransferId.set(
        l.transfer_id,
        (contagemTransferId.get(l.transfer_id) ?? 0) + 1,
      );
    }
  }

  let entrou = 0;
  let gastosVariaveis = 0;
  let fixasRealizadas = 0;
  let aportes = 0;
  let transferencias = 0;
  let empresa = 0;
  let naoClassificado = 0;

  // O escopo de uma transferência é do EVENTO (o transfer_id), não da linha
  // isolada — ver o comentário grande acima da função.
  const transferenciaEhEmpresa = new Set<string>();
  for (const l of canonizadas) {
    if (l.transfer_id && temTagEmpresa(l.notes)) {
      transferenciaEhEmpresa.add(l.transfer_id);
    }
  }
  const notaDoEvento = (transferId: string): string | null =>
    transferenciaEhEmpresa.has(transferId) ? TAG_EMPRESA : null;

  // Uma transferência é aporte quando a ponta que RECEBE está fora do orçamento.
  // Guardamos os transfer_id que têm alguma ponta DENTRO do orçamento e do MESMO
  // escopo pedido — sem o filtro de escopo aqui, um aporte marcado #empresa ainda
  // contaria como aporte pessoal só porque a outra perna do MESMO transfer_id
  // está dentro do orçamento (e vice-versa).
  const temPontaNoOrcamento = new Set(
    canonizadas
      .filter(
        l =>
          l.transfer_id &&
          !l.contaForaDoOrcamento &&
          ehDoEscopo(notaDoEvento(l.transfer_id), escopo),
      )
      .map(l => l.transfer_id as string),
  );

  // Transferência com QUALQUER perna fora do orçamento não é remanejo puro entre
  // contas do orçamento (é aporte, ou o espelho dele) — isso é propriedade de
  // TIPO DE CONTA, não de tag, então não filtra por escopo.
  const temPontaForaDoOrcamento = new Set(
    canonizadas
      .filter(l => l.transfer_id && l.contaForaDoOrcamento)
      .map(l => l.transfer_id as string),
  );

  for (const l of canonizadas) {
    if (ehTransacaoDeSaldoInicial(l)) continue;
    if (ehLinhaPai(l)) continue; // o valor real está nos filhos

    if (l.transfer_id) {
      // A tag decide de quem é o dinheiro mesmo numa transferência: um evento de
      // outro escopo não conta em lugar nenhum aqui — nem em aportes, nem em
      // naoEhGasto (mesma régua já usada para receita #empresa comum: dinheiro
      // que é da outra parte simplesmente não aparece no resultado deste escopo).
      if (!ehDoEscopo(notaDoEvento(l.transfer_id), escopo)) continue;

      const pareadaNoLote = (contagemTransferId.get(l.transfer_id) ?? 0) >= 2;

      if (!pareadaNoLote) {
        const { tipo, valor } = classificarPernaOrfa(
          l,
          extras.contaDaIrmaOrfa?.[l.transfer_id],
        );
        if (tipo === 'aporte') aportes += valor;
        else if (tipo === 'transferencia') transferencias += valor;
        else if (tipo === 'naoClassificado') naoClassificado += valor;
        continue;
      }

      const entrandoNoPatrimonio =
        l.amount > 0 &&
        l.contaForaDoOrcamento &&
        temPontaNoOrcamento.has(l.transfer_id);

      if (entrandoNoPatrimonio) {
        aportes += l.amount;
      } else if (
        l.amount < 0 &&
        !l.contaForaDoOrcamento &&
        !temPontaForaDoOrcamento.has(l.transfer_id)
      ) {
        transferencias += -l.amount;
      }
      continue;
    }

    // Linhas de conta fora do orçamento que não são transferência (ex.: rendimento
    // creditado na corretora) não entram no mês do orçamento.
    if (contaEhForaDoOrcamento(l)) continue;

    if (!ehDoEscopo(l.notes, escopo)) {
      if (l.amount < 0) empresa += -l.amount;
      continue;
    }

    if (l.amount > 0) {
      entrou += l.amount;
    } else if (l.schedule) {
      fixasRealizadas += -l.amount;
    } else {
      gastosVariaveis += -l.amount;
    }
  }

  const fixasPrevistas = extras.fixasPrevistas ?? 0;
  const livre =
    entrou - fixasRealizadas - fixasPrevistas - aportes - gastosVariaveis;

  return {
    entrou,
    gastosVariaveis,
    fixasRealizadas,
    fixasPrevistas,
    aportes,
    livre,
    naoEhGasto: { transferencias, empresa, naoClassificado },
  };
}
